import React, { useState, useEffect } from 'react';
import { Icon } from '@iconify/react';
import { useParams, Link } from 'react-router-dom';
import { decodeData } from '../../../utils/encodeAndEncode';
import { getModuleQuestions } from '../../../services/adminService';
import Loader from '../../../components/admin/Loader';

function AdminAssessmentView() {
    const { courseId, moduleId, contentId } = useParams();
    const decodedModuleId = decodeData(moduleId);
    const decodedContentId = decodeData(contentId);
    const [loading, setLoading] = useState(true);
    const [questions, setQuestions] = useState([]);

    useEffect(() => {
        const fetchQuestions = async () => {
            try {
                setLoading(true);
                const response = await getModuleQuestions({
                    module_id: decodedModuleId,
                    assessment_id: decodedContentId
                });
                if (response.success && response.data.response) {
                    setQuestions(response.data.response);
                }
            } catch (error) {
                console.error('Error fetching questions:', error);
            } finally {
                setLoading(false);
            }
        };

        if (decodedModuleId && decodedContentId) {
            fetchQuestions();
        }
    }, [decodedModuleId, decodedContentId]);

    if (loading) {
        return (
            <div className="flex justify-center items-center min-h-screen">
                <Loader />
            </div>
        );
    }

    return (
        <div className="px-4 md:px-8 py-5 max-w-6xl mx-auto">
            <div className="mb-4 flex items-center gap-3">
                <Link
                    to={`/admin/approvalRequest/view/${courseId}/content/${moduleId}`}
                    className="inline-flex items-center justify-center w-9 h-9 rounded-full border border-gray-200 text-gray-600 hover:bg-gray-50 no-underline"
                >
                    <Icon icon="fluent:arrow-left-24-regular" width={18} height={18} />
                </Link>
                <div>
                    <h4 className="mb-0 text-lg font-semibold text-gray-900">Course Assessment</h4>
                    <p className="text-xs text-gray-500 mt-0.5">View only mode</p>
                </div>
            </div>

            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 sm:p-5">
                {questions.length === 0 ? (
                    <div className="text-center py-4">
                        <p className="text-gray-600">No questions available</p>
                    </div>
                ) : (
                    questions.map((question, index) => (
                        <div className="bg-white rounded-lg shadow-sm border border-gray-100 mb-3 p-4" key={question.id}>
                            <div className="flex items-center gap-2 mb-3">
                                <span className="bg-primary text-white px-2 py-1 rounded text-sm">{index + 1}</span>
                                <h6 className="text-lg font-semibold mb-0">{question.question}</h6>
                            </div>
                            
                            {question.question_content_type !== 'text' && question.question_media_url && (
                                <div className="mb-3 bg-gray-50 rounded p-3">
                                    {question.question_content_type === 'image' ? (
                                        <img src={question.question_media_url} alt={question.question} className="max-w-full rounded" style={{ maxHeight: '300px' }} />
                                    ) : (
                                        <audio controls preload="metadata" className="w-full">
                                            <source src={question.question_media_url} type="audio/mpeg" />
                                        </audio>
                                    )}
                                </div>
                            )}

                            <div className="mb-2">
                                <small className="text-gray-500 uppercase text-xs">Options</small>
                            </div>
                            <div>
                                {question.formatted_options.map((option) => (
                                    <div key={option.key} className={`p-3 rounded-lg mb-2 ${option.isCorrect ? 'bg-green-50 border border-green-300' : 'bg-gray-50 border border-gray-200'}`}>
                                        <div className="flex items-center gap-3">
                                            <Icon icon={option.isCorrect ? "fluent:checkmark-circle-24-filled" : "fluent:circle-24-regular"} className={option.isCorrect ? "text-green-600" : "text-gray-500"} width="20" />
                                            <span>{option.content_type === 'text' ? option.content_value : option.text_fallback || option.content_value}</span>
                                        </div>
                                        {option.content_type !== 'text' && option.media_url && (
                                            <div className="ml-8 mt-2">
                                                {option.content_type === 'image' ? (
                                                    <img src={option.media_url} alt={option.text_fallback} className="rounded" style={{ maxHeight: '150px' }} />
                                                ) : (
                                                    <audio controls preload="metadata" className="w-full" style={{ maxWidth: '300px' }}>
                                                        <source src={option.media_url} type="audio/mpeg" />
                                                    </audio>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
}

export default AdminAssessmentView;
